import {
    Alert,
    Button,
    Classes,
    H4,
    Intent,
    Menu,
    MenuItem,
    Popover,
    Tag
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { AgGridReact } from "ag-grid-react";
import classNames from "classnames";
import {
    BulkScenarioSettingSource,
    segmentTrackApplyBulkScenarioSetting,
    segmentTrackFormatBulkScenarioTitles,
    segmentTrackRemoveBulkScenario,
    segmentTrackViewBulkScenarioAssumptions,
    segmentTrackViewBulkScenarioFileIssues
} from "in_queue/analytics";
import { useStageConfig } from "in_queue/contexts/ClusterContext";
import { useCustomUpgrades } from "in_queue/contexts/CustomUpgradesContext";
import { useCustomUpgradesStatus } from "in_queue/hooks/useCustomUpgradesStatus";
import { CostAssumption, ScenarioStage } from "in_queue/types/scenarioType";
import { useCallback, useMemo, useRef, useState } from "react";
import { scenarioCount } from "../ScenarioRerun/bulkRerunToast";
import { SelectionBar } from "../SelectionBar/SelectionBar";
import { BLUEPRINT_SORT_ICONS } from "../tables/agGrid/sortIcons";
import { BaseAgGrid } from "../tables/base/BaseAgGrid";
import {
    BulkScenarioDraft,
    BulkScenarioDraftPatch,
    BulkScenarioSettings,
    bulkScenarioWarningCount,
    sharedBulkScenarioSetting
} from "./bulkScenarioDrafts";
import { BulkScenarioFormatTitlesDialog } from "./BulkScenarioFormatTitlesDialog";
import {
    BulkCostAssumptionsDialog,
    BulkCustomUpgradeDialog
} from "./BulkScenarioSettingDialogs";
import css from "./BulkScenarioTable.module.scss";
import {
    BulkScenarioRow,
    bulkScenarioTableColumns,
    DEFAULT_COL_DEF
} from "./bulkScenarioTableColumns";
import { BulkScenarioUploadLabel } from "./bulkScenarioUploadLabels";
import { BulkScenarioWarningsDialog } from "./BulkScenarioWarningsDialog";
import {
    ScenarioTitleFormat,
    SCENARIO_TITLE_FORMATS,
    TextScenarioTitleFormat
} from "./scenarioTitleFormat";
import { useBulkScenarioSize } from "./useBulkScenarioSize";

// Matches the project page's scenarios table, whose two-line Sensitivities
// cell this table shares.
const ROW_HEIGHT = 56;
const HEADER_ROW_HEIGHT = 42;

const countLabel = (count: number, overLimitCount: number): string =>
    overLimitCount > 0
        ? `${scenarioCount(count)}, ${overLimitCount} over limit`
        : scenarioCount(count);

type OpenDialog =
    | { readonly kind: "cost" }
    | {
          readonly kind: "upgrade";
          readonly draftIds: ReadonlySet<string>;
          readonly source: BulkScenarioSettingSource;
      }
    | {
          readonly kind: "format";
          readonly format: TextScenarioTitleFormat;
          readonly drafts: ReadonlyArray<BulkScenarioDraft>;
      }
    | { readonly kind: "warnings"; readonly draftId: string }
    | { readonly kind: "remove"; readonly draftId: string };

/** List the uploaded drafts, edit their titles and settings, and link to each one's sensitivities. */
export const BulkScenarioTable: React.FC<{
    readonly drafts: ReadonlyArray<BulkScenarioDraft>;
    readonly overLimitCount: number;
    /** Must keep a stable identity, since the grid's columns are built from it. */
    readonly onUpdateDrafts: (
        ids: ReadonlySet<string>,
        patch: BulkScenarioDraftPatch
    ) => void;
    readonly onRemoveDraft: (id: string) => void;
    /** Must keep a stable identity, since the grid's columns are built from it. */
    readonly onOpenDraft: (id: string) => void;
    readonly scenarioStage: ScenarioStage;
    readonly defaultCostAssumptions: CostAssumption;
    readonly projectId: string;
    readonly onlyExclusionSupported: boolean;
}> = ({
    drafts,
    overLimitCount,
    onUpdateDrafts,
    onRemoveDraft,
    onOpenDraft,
    scenarioStage,
    defaultCostAssumptions,
    projectId,
    onlyExclusionSupported
}) => {
    const stageConfig = useStageConfig();
    const upgradesStatus = useCustomUpgradesStatus();
    const customUpgrades = useCustomUpgrades();
    const sizeOf = useBulkScenarioSize();
    const gridRef = useRef<AgGridReact<BulkScenarioRow>>(null);
    const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
        new Set()
    );
    const [openDialog, setOpenDialog] = useState<OpenDialog>();

    const costAssumptionTypes =
        stageConfig.supportedCostAssumptionTypes[scenarioStage];
    const showCostAssumptions = costAssumptionTypes.length > 0;
    const canSetCostAssumptions = costAssumptionTypes.length > 1;
    const canSetCustomUpgrade = upgradesStatus === "enabled";

    const rows: BulkScenarioRow[] = useMemo(
        () =>
            drafts.map((draft) => {
                const { metadata, exceedsMwThreshold } = sizeOf(draft);
                const upgrade =
                    customUpgrades === "loading"
                        ? undefined
                        : customUpgrades.find(
                              (candidate) =>
                                  candidate.id === draft.customUpgradeId
                          );
                return {
                    draft,
                    sizeMetadata: metadata,
                    exceedsMwThreshold,
                    warningCount: bulkScenarioWarningCount(draft.uploadNotes),
                    customUpgradeName: upgrade?.name
                };
            }),
        [drafts, sizeOf, customUpgrades]
    );

    const openSensitivities = useCallback(
        (draftId: string) => {
            segmentTrackViewBulkScenarioAssumptions({ projectId });
            onOpenDraft(draftId);
        },
        [projectId, onOpenDraft]
    );

    const showWarnings = useCallback(
        (draftId: string) => {
            segmentTrackViewBulkScenarioFileIssues({
                projectId,
                issue: "warnings"
            });
            setOpenDialog({ kind: "warnings", draftId });
        },
        [projectId]
    );

    const renameDraft = useCallback(
        (draftId: string, title: string) =>
            onUpdateDrafts(new Set([draftId]), { title }),
        [onUpdateDrafts]
    );

    const confirmRemoval = useCallback(
        (draftId: string) => setOpenDialog({ kind: "remove", draftId }),
        []
    );

    const editCustomUpgrade = useCallback(
        (draftId: string) =>
            setOpenDialog({
                kind: "upgrade",
                draftIds: new Set([draftId]),
                source: "row"
            }),
        []
    );

    const columnDefs = useMemo(
        () =>
            bulkScenarioTableColumns({
                showCostAssumptions,
                showCustomUpgrade: canSetCustomUpgrade,
                onRename: renameDraft,
                onOpenSensitivities: openSensitivities,
                onEditCustomUpgrade: editCustomUpgrade,
                onShowWarnings: showWarnings,
                onRemove: confirmRemoval
            }),
        [
            showCostAssumptions,
            canSetCustomUpgrade,
            renameDraft,
            openSensitivities,
            editCustomUpgrade,
            showWarnings,
            confirmRemoval
        ]
    );

    const selectedDrafts = drafts.filter((draft) => selectedIds.has(draft.id));
    const hasSelection = selectedDrafts.length > 0;
    const warningsDraft =
        openDialog?.kind === "warnings"
            ? drafts.find((draft) => draft.id === openDialog.draftId)
            : undefined;
    const removalDraft =
        openDialog?.kind === "remove"
            ? drafts.find((draft) => draft.id === openDialog.draftId)
            : undefined;

    const closeDialog = () => setOpenDialog(undefined);

    const apply = (
        ids: ReadonlySet<string>,
        settings: Partial<BulkScenarioSettings>
    ) => {
        onUpdateDrafts(ids, settings);
        closeDialog();
    };

    const applyRunOnSubmit = (runOnSubmit: boolean) => {
        segmentTrackApplyBulkScenarioSetting({
            projectId,
            setting: "run_on_submit",
            value: runOnSubmit,
            numScenarios: selectedIds.size,
            source: "selection_bar"
        });
        apply(selectedIds, { runOnSubmit });
    };

    const formatSelectedTitles = (
        format: ScenarioTitleFormat,
        formatTitle: (title: string) => string
    ) => {
        segmentTrackFormatBulkScenarioTitles({
            projectId,
            numScenarios: selectedIds.size,
            format: format.key
        });
        onUpdateDrafts(selectedIds, (draft) => ({
            title: formatTitle(draft.title)
        }));
    };

    const chooseTitleFormat = (format: ScenarioTitleFormat) => {
        if (format.kind === "instant") {
            formatSelectedTitles(format, format.apply);
            return;
        }
        const selectedInGridOrder: BulkScenarioDraft[] = [];
        gridRef.current?.api.forEachNodeAfterFilterAndSort(({ data }) => {
            if (data !== undefined && selectedIds.has(data.draft.id)) {
                selectedInGridOrder.push(data.draft);
            }
        });
        setOpenDialog({ kind: "format", format, drafts: selectedInGridOrder });
    };

    return (
        <div className={css["BulkScenarioTable"]}>
            <div className={css["BulkScenarioTable-heading"]}>
                <H4 className={css["BulkScenarioTable-heading-text"]}>
                    Scenarios
                </H4>
                <Tag
                    round
                    minimal
                    intent={overLimitCount > 0 ? Intent.WARNING : Intent.NONE}
                >
                    {countLabel(drafts.length, overLimitCount)}
                </Tag>
            </div>
            <SelectionBar
                className={css["BulkScenarioTable-selection-bar"]}
                itemName="scenario"
                selectedCount={selectedIds.size}
                emptyLabel="Select scenarios to change their settings"
                onClear={() => gridRef.current?.api.deselectAll()}
            >
                <Popover
                    disabled={!hasSelection}
                    placement="bottom-end"
                    content={
                        <Menu>
                            {SCENARIO_TITLE_FORMATS.map((format) => (
                                <MenuItem
                                    key={format.key}
                                    multiline
                                    text={
                                        <>
                                            {format.label}
                                            {format.description !==
                                                undefined && (
                                                <div
                                                    className={classNames(
                                                        Classes.TEXT_MUTED,
                                                        Classes.TEXT_SMALL
                                                    )}
                                                >
                                                    {format.description}
                                                </div>
                                            )}
                                        </>
                                    }
                                    onClick={() => chooseTitleFormat(format)}
                                />
                            ))}
                        </Menu>
                    }
                >
                    <Button
                        text={BulkScenarioUploadLabel.FormatTitles}
                        endIcon={IconNames.CARET_DOWN}
                        disabled={!hasSelection}
                    />
                </Popover>
                {canSetCostAssumptions && (
                    <Button
                        text={BulkScenarioUploadLabel.SetCostAssumptions}
                        disabled={!hasSelection}
                        onClick={() => setOpenDialog({ kind: "cost" })}
                    />
                )}
                {canSetCustomUpgrade && (
                    <Button
                        text={BulkScenarioUploadLabel.SetCustomUpgrade}
                        disabled={!hasSelection}
                        onClick={() =>
                            setOpenDialog({
                                kind: "upgrade",
                                draftIds: selectedIds,
                                source: "selection_bar"
                            })
                        }
                    />
                )}
                <Popover
                    disabled={!hasSelection}
                    placement="bottom-end"
                    content={
                        <Menu>
                            <MenuItem
                                icon={IconNames.PLAY}
                                text="Run now"
                                onClick={() => applyRunOnSubmit(true)}
                            />
                            <MenuItem
                                icon={IconNames.DOCUMENT}
                                text="Save as draft"
                                onClick={() => applyRunOnSubmit(false)}
                            />
                        </Menu>
                    }
                >
                    <Button
                        text={BulkScenarioUploadLabel.SetRunNow}
                        endIcon={IconNames.CARET_DOWN}
                        disabled={!hasSelection}
                    />
                </Popover>
            </SelectionBar>
            <BaseAgGrid<BulkScenarioRow>
                ref={gridRef}
                className={css["BulkScenarioTable-grid"]}
                loadingData={rows}
                columnDefs={columnDefs}
                defaultColDef={DEFAULT_COL_DEF}
                icons={BLUEPRINT_SORT_ICONS}
                height="auto"
                rowHeight={ROW_HEIGHT}
                headerHeight={HEADER_ROW_HEIGHT}
                rowSelection="multiple"
                suppressRowClickSelection
                suppressClickEdit
                suppressCellFocus
                getRowId={({ data }) => data.draft.id}
                onSelectionChanged={({ api }) =>
                    setSelectedIds(
                        new Set(
                            api.getSelectedRows().map((row) => row.draft.id)
                        )
                    )
                }
                analyticsTableName="Bulk Scenario Upload"
            />
            {openDialog?.kind === "cost" && (
                <BulkCostAssumptionsDialog
                    count={selectedDrafts.length}
                    scenarioStage={scenarioStage}
                    initialValue={
                        sharedBulkScenarioSetting(
                            selectedDrafts,
                            "costAssumptions"
                        ) ?? defaultCostAssumptions
                    }
                    onClose={closeDialog}
                    onApply={(costAssumptions) => {
                        segmentTrackApplyBulkScenarioSetting({
                            projectId,
                            setting: "cost_assumptions",
                            value: costAssumptions.type,
                            numScenarios: selectedIds.size,
                            source: "selection_bar"
                        });
                        apply(selectedIds, { costAssumptions });
                    }}
                />
            )}
            {openDialog?.kind === "format" && (
                <BulkScenarioFormatTitlesDialog
                    format={openDialog.format}
                    drafts={openDialog.drafts}
                    onClose={closeDialog}
                    onApply={(text) => {
                        formatSelectedTitles(openDialog.format, (title) =>
                            openDialog.format.apply(title, text)
                        );
                        closeDialog();
                    }}
                />
            )}
            {openDialog?.kind === "upgrade" && (
                <BulkCustomUpgradeDialog
                    count={openDialog.draftIds.size}
                    initialValue={
                        sharedBulkScenarioSetting(
                            drafts.filter((draft) =>
                                openDialog.draftIds.has(draft.id)
                            ),
                            "customUpgradeId"
                        ) ?? null
                    }
                    onClose={closeDialog}
                    onApply={(customUpgradeId) => {
                        segmentTrackApplyBulkScenarioSetting({
                            projectId,
                            setting: "custom_upgrade",
                            value: customUpgradeId ?? "",
                            numScenarios: openDialog.draftIds.size,
                            source: openDialog.source
                        });
                        apply(openDialog.draftIds, { customUpgradeId });
                    }}
                />
            )}
            {warningsDraft !== undefined && (
                <BulkScenarioWarningsDialog
                    draft={warningsDraft}
                    onlyExclusionSupported={onlyExclusionSupported}
                    onClose={closeDialog}
                />
            )}
            {removalDraft !== undefined && (
                <Alert
                    isOpen
                    intent={Intent.DANGER}
                    icon={IconNames.TRASH}
                    cancelButtonText="Cancel"
                    confirmButtonText="Remove"
                    onCancel={closeDialog}
                    onConfirm={() => {
                        segmentTrackRemoveBulkScenario({ projectId });
                        onRemoveDraft(removalDraft.id);
                        closeDialog();
                    }}
                >
                    <p>
                        Remove <b>{removalDraft.title}</b> from this upload?
                    </p>
                </Alert>
            )}
        </div>
    );
};
